import { useEffect, useState } from "react";
import api, { formatApiError } from "../api/client";
import { Apple, Check, AlertCircle, Save, Heart, Sparkles, Wallet, MapPin, Utensils, Download, BookOpen } from "lucide-react";
import MealImage from "./MealImage";
import RecipeModal from "./RecipeModal";

const PHASES = [
  { id: "metabolic", label: "Metabolic Training", desc: "Maintenance — work capacity, metabolic flexibility.", carbs: 45, protein: 30, fat: 25, kcal: "maintenance" },
  { id: "mechanical", label: "Mechanical Tension", desc: "Building — slight surplus for tissue & growth.", carbs: 38, protein: 38, fat: 24, kcal: "+200–300" },
  { id: "systemic", label: "Systemic Training", desc: "Cutting — gentle deficit, higher fat for energy.", carbs: 20, protein: 35, fat: 45, kcal: "−200–300" },
];

const ACTIVITY = [
  { id: "sedentary", label: "Sedentary (desk, no training)" },
  { id: "light", label: "Light (1–2 sessions / week)" },
  { id: "moderate", label: "Moderate (3–4 sessions / week)" },
  { id: "active", label: "Active (5–6 sessions / week)" },
  { id: "very_active", label: "Very active (daily, athlete)" },
];

const empty = {
  age: "", sex: "", height_cm: "", weight_kg: "", muscle_mass_kg: "", fat_pct: "",
  activity_factor: "moderate", goal: "", phase: "",
  current_eating: "",
  allergies: "", menopause: false, conditions: "", food_restrictions: "", vitamin_notes: "",
  weekly_food_budget: "", location: "",
};

/**
 * Reusable nutrition profile editor + calculated card.
 * Mode "client" -> uses /api/nutrition/profile
 * Mode "admin"  -> uses /api/admin/nutrition/profiles/{clientId}
 */
export default function NutritionProfileEditor({ mode = "client", clientId = null, clientName = null }) {
  const isAdmin = mode === "admin";
  const baseUrl = isAdmin ? `/admin/nutrition/profiles/${clientId}` : "/nutrition/profile";

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isAdmin && !clientId) return;
    api.get(baseUrl)
      .then((r) => {
        if (r.data) {
          setProfile(r.data);
          const f = { ...empty };
          for (const k of Object.keys(empty)) {
            if (r.data[k] != null) f[k] = r.data[k];
          }
          setForm(f);
        } else {
          setProfile(null);
          setForm(empty);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const save = async (e) => {
    e?.preventDefault();
    setBusy(true); setError(""); setSavedAt(null);
    try {
      const payload = {
        age: num(form.age),
        sex: form.sex || null,
        height_cm: num(form.height_cm),
        weight_kg: num(form.weight_kg),
        muscle_mass_kg: num(form.muscle_mass_kg),
        fat_pct: num(form.fat_pct),
        activity_factor: form.activity_factor || "moderate",
        goal: form.goal || null,
        phase: form.phase || null,
        current_eating: form.current_eating || null,
        allergies: form.allergies || null,
        menopause: !!form.menopause,
        conditions: form.conditions || null,
        food_restrictions: form.food_restrictions || null,
        vitamin_notes: form.vitamin_notes || null,
        weekly_food_budget: form.weekly_food_budget || null,
        location: form.location || null,
      };
      const { data } = await api.put(baseUrl, payload);
      setProfile(data);
      setSavedAt(new Date());
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const phaseCfg = PHASES.find((p) => p.id === form.phase);

  // ---- Plan generator ----
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState("");
  const generate = async () => {
    setGenBusy(true); setGenError("");
    try {
      const url = isAdmin ? `/admin/nutrition/profiles/${clientId}/generate` : "/nutrition/profile/generate";
      const { data } = await api.post(url);
      setProfile(data);
    } catch (e) {
      const status = e.response?.status;
      const friendly = status === 502
        ? "Sopfie is taking a breath — please try again in a moment."
        : (formatApiError(e.response?.data?.detail) || e.message);
      setGenError(friendly);
    } finally {
      setGenBusy(false);
    }
  };

  // ---- Recipe modal ----
  const [recipeModal, setRecipeModal] = useState({ open: false, slot: null, opt: null });
  const [recipeBusy, setRecipeBusy] = useState(false);
  const [recipeError, setRecipeError] = useState("");

  const openRecipe = async (slotIdx, optIdx, option) => {
    setRecipeModal({ open: true, slot: slotIdx, opt: optIdx });
    setRecipeError("");
    if (option?.recipe) return; // already cached on this option
    if (isAdmin) {
      // Admin doesn't have a recipe-generate endpoint scoped to client; ask client to generate it
      setRecipeError("The client can generate the recipe themselves from their dashboard.");
      return;
    }
    setRecipeBusy(true);
    try {
      const { data } = await api.post("/nutrition/profile/recipe", { slot_index: slotIdx, option_index: optIdx });
      setProfile(data);
    } catch (e) {
      const status = e.response?.status;
      setRecipeError(status === 502 ? "Sopfie is taking a breath — please try again in a moment." : (formatApiError(e.response?.data?.detail) || e.message));
    } finally {
      setRecipeBusy(false);
    }
  };

  const closeRecipe = () => setRecipeModal({ open: false, slot: null, opt: null });

  const recipeCurrent = (() => {
    if (!recipeModal.open || recipeModal.slot == null) return null;
    const slot = profile?.meal_plan?.[recipeModal.slot];
    if (!slot) return null;
    const options = slot.options || [slot];
    return options[recipeModal.opt] || null;
  })();

  // ---- Download (print to PDF) ----
  const downloadPlan = () => {
    document.body.classList.add("printing-plan");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-plan"), 800);
  };

  return (
    <div className="space-y-6">
    <form onSubmit={save} className="grid lg:grid-cols-12 gap-6 no-print" data-testid="nutrition-profile-editor">
      {/* LEFT: numbers + phase */}
      <div className="lg:col-span-7 space-y-5">
        <div className="card-soft p-6">
          <h3 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2">
            <Apple size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            {isAdmin ? `${clientName || "Client"} — body & phase` : "Your body & phase"}
          </h3>
          <p className="mt-1 text-sm text-[color:var(--ink-2)]">
            Numbers from your latest measurement. The targets below update as you save.
          </p>

          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            <Field label="Age" testid="np-age"><input className="input-warm" type="number" min={0} value={form.age} onChange={(e) => upd("age", e.target.value)} data-testid="np-age" /></Field>
            <Field label="Height (cm)" testid="np-height"><input className="input-warm" type="number" step="0.1" value={form.height_cm} onChange={(e) => upd("height_cm", e.target.value)} data-testid="np-height" /></Field>
            <Field label="Sex"><select className="input-warm" value={form.sex || ""} onChange={(e) => upd("sex", e.target.value)} data-testid="np-sex"><option value="">—</option><option value="female">Female</option><option value="male">Male</option><option value="other">Other</option></select></Field>
            <Field label="Weight (kg)"><input required className="input-warm" type="number" step="0.1" value={form.weight_kg} onChange={(e) => upd("weight_kg", e.target.value)} data-testid="np-weight" /></Field>
            <Field label="Muscle mass (kg)"><input className="input-warm" type="number" step="0.1" value={form.muscle_mass_kg} onChange={(e) => upd("muscle_mass_kg", e.target.value)} data-testid="np-muscle" /></Field>
            <Field label="Body fat (%)"><input required className="input-warm" type="number" step="0.1" value={form.fat_pct} onChange={(e) => upd("fat_pct", e.target.value)} data-testid="np-fat" /></Field>
          </div>

          <div className="mt-4">
            <p className="overline mb-2">Activity level</p>
            <div className="flex flex-wrap gap-2">
              {ACTIVITY.map((a) => (
                <button type="button" key={a.id} onClick={() => upd("activity_factor", a.id)} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${form.activity_factor === a.id ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--ink-2)] hover:border-[color:var(--ink)]"}`} data-testid={`np-activity-${a.id}`}>{a.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="card-soft p-6">
          <h3 className="font-serif text-2xl text-[color:var(--ink)]">Training phase</h3>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">{isAdmin ? "Set the phase you have this client in." : "The phase Sopfie has you in right now."}</p>
          <div className="mt-4 grid md:grid-cols-3 gap-3">
            {PHASES.map((p) => (
              <button type="button" key={p.id} onClick={() => upd("phase", p.id)} className={`text-left p-4 rounded-2xl border transition-colors ${form.phase === p.id ? "bg-[var(--bg-2)] border-[color:var(--accent)]/60" : "border-[color:var(--line)] hover:border-[color:var(--ink)]"}`} data-testid={`np-phase-${p.id}`}>
                <p className="overline">{p.kcal}</p>
                <p className="font-serif text-lg text-[color:var(--ink)] mt-1">{p.label}</p>
                <p className="text-xs text-[color:var(--ink-2)] mt-1.5">{p.desc}</p>
                <p className="mt-3 text-xs text-[color:var(--ink-3)]">{p.carbs}C · {p.protein}P · {p.fat}F</p>
              </button>
            ))}
          </div>
        </div>

        <div className="card-soft p-6">
          <h3 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2"><Heart size={16} strokeWidth={1.4} className="text-[color:var(--accent)]" /> Lifestyle & limitations</h3>
          <p className="text-sm text-[color:var(--ink-2)] mt-1">Anything Sopfie should hold while making your numbers feel like home.</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <Field label="Goal"><input className="input-warm" placeholder="Feel strong; lower belly tension; sleep deeper…" value={form.goal} onChange={(e) => upd("goal", e.target.value)} data-testid="np-goal" /></Field>
            <Field label="Allergies"><input className="input-warm" placeholder="Nuts, gluten, dairy…" value={form.allergies} onChange={(e) => upd("allergies", e.target.value)} data-testid="np-allergies" /></Field>
            <Field label="Conditions / disease"><input className="input-warm" placeholder="Hashimoto's, IBS, hypertension…" value={form.conditions} onChange={(e) => upd("conditions", e.target.value)} data-testid="np-conditions" /></Field>
            <Field label="Food to avoid"><input className="input-warm" placeholder="Cruciferous; high-FODMAP; nightshades…" value={form.food_restrictions} onChange={(e) => upd("food_restrictions", e.target.value)} data-testid="np-restrictions" /></Field>
          </div>
          <Field label="Vitamins / supplements (and why)" className="mt-3">
            <textarea rows={2} className="input-warm" placeholder="Iron, magnesium glycinate, vit. D…" value={form.vitamin_notes} onChange={(e) => upd("vitamin_notes", e.target.value)} data-testid="np-vitamins" />
          </Field>
          <label className="mt-3 flex items-center gap-2 text-sm text-[color:var(--ink-2)] cursor-pointer">
            <input type="checkbox" checked={!!form.menopause} onChange={(e) => upd("menopause", e.target.checked)} data-testid="np-menopause" /> In peri- or post-menopause
          </label>
          <Field label="What you usually eat in a day" className="mt-4">
            <textarea rows={4} className="input-warm" placeholder="Honest snapshot of a normal day — coffee on rise, oats, rye-bread lunch, evening pasta, glass of wine on Friday…" value={form.current_eating} onChange={(e) => upd("current_eating", e.target.value)} data-testid="np-current-eating" />
          </Field>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Weekly food budget"><div className="relative"><Wallet size={14} className="absolute left-3 top-3 text-[color:var(--ink-3)]" /><input className="input-warm pl-9" placeholder="e.g. 600 DKK / week" value={form.weekly_food_budget} onChange={(e) => upd("weekly_food_budget", e.target.value)} data-testid="np-budget" /></div></Field>
            <Field label="Where you live (cuisine)"><div className="relative"><MapPin size={14} className="absolute left-3 top-3 text-[color:var(--ink-3)]" /><input className="input-warm pl-9" placeholder="e.g. Copenhagen, DK" value={form.location} onChange={(e) => upd("location", e.target.value)} data-testid="np-location" /></div></Field>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button disabled={busy} className="btn-primary" data-testid="np-save"><Save size={14} strokeWidth={1.6} /> {busy ? "Saving…" : "Save & calculate"}</button>
          {savedAt && <span className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Check size={12} className="text-[color:var(--accent)]" /> Saved {savedAt.toLocaleTimeString()}</span>}
          {error && <span className="text-sm text-[color:var(--accent-2)] flex items-center gap-1" data-testid="np-error"><AlertCircle size={14} /> {error}</span>}
        </div>
      </div>

      {/* RIGHT: calculated card */}
      <aside className="lg:col-span-5">
        <div className="card-soft p-7 sticky top-24" data-testid="nutrition-targets">
          <p className="overline">Your numbers</p>
          {!profile?.maintenance_kcal ? (
            <p className="mt-3 font-serif text-xl text-[color:var(--ink)]">
              Save your weight & body fat to see your maintenance, target, and macro split.
            </p>
          ) : (
            <>
              <h3 className="mt-2 font-serif text-3xl text-[color:var(--ink)] tracking-tight">
                {profile.target_kcal ? <>{profile.target_kcal} <span className="text-lg text-[color:var(--ink-2)] font-sans tracking-normal">kcal / day</span></> : <>Maintenance: {profile.maintenance_kcal} kcal</>}
              </h3>
              {profile.phase_label && (
                <p className="mt-1 text-sm text-[color:var(--accent-2)]">{profile.phase_label} · {phaseCfg ? phaseCfg.kcal : ""} kcal</p>
              )}
              {profile.phase_blurb && <p className="mt-2 text-sm text-[color:var(--ink-2)] italic">{profile.phase_blurb}</p>}

              <div className="mt-6 grid grid-cols-3 gap-3">
                <Stat label="Carbs" value={profile.carbs_g} unit="g" pct={phaseCfg?.carbs} />
                <Stat label="Protein" value={profile.protein_g} unit="g" pct={phaseCfg?.protein} />
                <Stat label="Fat" value={profile.fat_g} unit="g" pct={phaseCfg?.fat} />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3 text-xs">
                <Mini label="Lean body mass" value={profile.lbm_kg ? `${profile.lbm_kg} kg` : "—"} />
                <Mini label="BMR" value={profile.bmr_kcal ? `${profile.bmr_kcal} kcal` : "—"} />
                <Mini label="Maintenance" value={profile.maintenance_kcal ? `${profile.maintenance_kcal} kcal` : "—"} />
                <Mini label="Activity" value={(profile.activity_factor || "moderate").replace("_", " ")} />
              </div>
            </>
          )}

          {profile && (profile.current_eating || profile.allergies || profile.conditions || profile.menopause) && (
            <div className="mt-7 pt-5 border-t border-[color:var(--line-soft)] text-sm text-[color:var(--ink-2)] space-y-2">
              {profile.current_eating && <p><span className="overline mr-2">Today</span>{profile.current_eating}</p>}
              {profile.allergies && <p><span className="overline mr-2">Allergies</span>{profile.allergies}</p>}
              {profile.conditions && <p><span className="overline mr-2">Conditions</span>{profile.conditions}</p>}
              {profile.menopause && <p className="text-[color:var(--accent-2)]">In peri/post-menopause — adjust accordingly.</p>}
            </div>
          )}
        </div>
      </aside>
    </form>

    {/* AI-GENERATED MEAL PLAN */}
    <div className="card-soft p-6 md:p-7" data-testid="meal-plan-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Personalised plan</p>
          <h3 className="font-serif text-2xl text-[color:var(--ink)] flex items-center gap-2 mt-1">
            <Utensils size={18} strokeWidth={1.4} className="text-[color:var(--accent)]" />
            {isAdmin ? `${clientName || "Client"}'s` : "Your"} meal plan
          </h3>
          <p className="text-sm text-[color:var(--ink-2)] mt-1 max-w-xl">
            Built from foods {isAdmin ? "they" : "you"} already eat, matched to {isAdmin ? "their" : "your"} numbers, lifestyle and budget.
          </p>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={genBusy || !profile?.target_kcal && !profile?.maintenance_kcal}
          className="btn-primary"
          data-testid="generate-plan-btn"
        >
          <Sparkles size={14} strokeWidth={1.6} /> {genBusy ? "Cooking up your plan…" : profile?.meal_plan ? "Regenerate plan" : "Generate plan"}
        </button>
      </div>

      {genError && <p className="mt-3 text-sm text-[color:var(--accent-2)]" data-testid="plan-error">{genError}</p>}

      {!profile?.meal_plan && !genBusy && (
        <p className="mt-5 text-sm text-[color:var(--ink-3)] italic font-serif" data-testid="plan-empty">
          Save the profile first, then press <strong>Generate plan</strong>. {isAdmin ? "The client" : "You"} will see meals built from {isAdmin ? "their" : "your"} usual foods, matched to {isAdmin ? "their" : "your"} macros and budget — plus restaurant and travel alternatives.
        </p>
      )}

      {profile?.guidance && (
        <p className="mt-5 font-serif text-lg text-[color:var(--ink)] italic leading-relaxed border-l-2 border-[color:var(--accent)]/40 pl-4" data-testid="plan-guidance">
          {profile.guidance}
        </p>
      )}

      {profile?.meal_plan?.length > 0 && (
        <div className="mt-6 space-y-8" data-testid="plan-meals">
          {profile.meal_plan.map((slot, si) => {
            // Backward compatibility: old format had {label, name, items, ...} per entry; new format has {label, options:[...]}.
            const options = Array.isArray(slot.options) && slot.options.length
              ? slot.options
              : [{
                  name: slot.name, items: slot.items,
                  kcal: slot.kcal, carbs_g: slot.carbs_g, protein_g: slot.protein_g, fat_g: slot.fat_g,
                  why: slot.why, image_query: slot.image_query,
                }];
            const tKcal = slot.target_kcal || (options[0] && options[0].kcal);
            const tC = slot.target_carbs_g, tP = slot.target_protein_g, tF = slot.target_fat_g;
            return (
              <section key={si} data-testid={`plan-slot-${si}`}>
                <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <p className="overline">{slot.label || `Meal ${si + 1}`}</p>
                    <h4 className="font-serif text-2xl text-[color:var(--ink)] tracking-tight">
                      {options.length > 1 ? `${options.length} ways to do ${(slot.label || "this meal").toLowerCase()}` : slot.label || "Meal"}
                    </h4>
                  </div>
                  {tKcal && (
                    <p className="text-xs text-[color:var(--ink-3)]">
                      ~{Math.round(tKcal)} kcal
                      {tC != null && tP != null && tF != null && ` · ${Math.round(tC)}C · ${Math.round(tP)}P · ${Math.round(tF)}F`}
                    </p>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {options.map((m, oi) => {
                      return (
                        <article key={oi} className="rounded-2xl border border-[color:var(--line-soft)] bg-[var(--bg)] overflow-hidden flex flex-col" data-testid={`plan-option-${si}-${oi}`}>
                          <div className="relative aspect-[5/4]">
                            <MealImage name={m.name} query={m.image_query} eager={si === 0 && oi < 2} />
                            <div className="absolute top-2 left-2 right-2 flex items-baseline justify-between gap-1 text-[10px] text-white pointer-events-none">
                              <span className="px-1.5 py-0.5 rounded-full bg-[rgba(42,39,36,0.6)] backdrop-blur-sm uppercase tracking-widest">#{oi + 1}</span>
                              {m.kcal != null && <span className="px-1.5 py-0.5 rounded-full bg-[rgba(42,39,36,0.6)] backdrop-blur-sm">{Math.round(m.kcal)}</span>}
                            </div>
                          </div>
                          <div className="p-3.5 flex-1 flex flex-col">
                            <h5 className="font-serif text-base text-[color:var(--ink)] leading-snug">{m.name}</h5>
                            {m.items && <p className="mt-1.5 text-xs text-[color:var(--ink-2)] leading-relaxed">{m.items}</p>}
                            <div className="mt-2 flex gap-2 flex-wrap text-[11px] text-[color:var(--ink-3)]">
                              {m.carbs_g != null && <span><b className="text-[color:var(--ink)]">{Math.round(m.carbs_g)}</b>C</span>}
                              {m.protein_g != null && <span><b className="text-[color:var(--ink)]">{Math.round(m.protein_g)}</b>P</span>}
                              {m.fat_g != null && <span><b className="text-[color:var(--ink)]">{Math.round(m.fat_g)}</b>F</span>}
                            </div>
                            <button
                              type="button"
                              onClick={() => openRecipe(si, oi, m)}
                              className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-[color:var(--ink)] hover:text-[color:var(--accent)] border border-[color:var(--line)] hover:border-[color:var(--accent)]/60 rounded-full px-3 py-1.5 transition-colors no-print"
                              data-testid={`recipe-btn-${si}-${oi}`}
                            >
                              <BookOpen size={12} strokeWidth={1.6} /> {m.recipe ? "View recipe" : "Get recipe"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {profile?.eating_out?.length > 0 && (
        <div className="mt-8 pt-6 border-t border-[color:var(--line-soft)]" data-testid="plan-eating-out">
          <p className="overline">When life happens</p>
          <h4 className="mt-1 font-serif text-xl text-[color:var(--ink)]">Out, away, on holiday — pick from these.</h4>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {profile.eating_out.map((e, i) => (
              <div key={i} className="rounded-2xl border border-[color:var(--line-soft)] p-4 bg-[var(--bg)]" data-testid={`plan-scenario-${i}`}>
                <p className="overline">{e.scenario}</p>
                <ul className="mt-2 space-y-1.5 text-sm text-[color:var(--ink)]">
                  {(e.options || []).map((o, j) => (
                    <li key={j} className="leading-snug">• {o}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {profile?.plan_generated_at && (
        <p className="mt-6 text-xs text-[color:var(--ink-3)]">
          Last generated {new Date(profile.plan_generated_at).toLocaleString()}
        </p>
      )}
    </div>

    <RecipeModal
      open={recipeModal.open}
      onClose={closeRecipe}
      name={recipeCurrent?.name || ""}
      kcal={recipeCurrent?.kcal}
      recipe={recipeCurrent?.recipe}
      loading={recipeBusy}
      error={recipeError}
    />
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline block mb-1">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value, unit, pct }) {
  return (
    <div className="rounded-xl border border-[color:var(--line-soft)] p-3 bg-[var(--bg)]">
      <p className="overline">{label}</p>
      <p className="mt-1 font-serif text-2xl text-[color:var(--ink)] leading-tight">{value ?? "—"}<span className="text-sm text-[color:var(--ink-3)] font-sans ml-1">{unit}</span></p>
      {pct != null && <p className="text-xs text-[color:var(--ink-3)] mt-1">{pct}%</p>}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <p className="overline">{label}</p>
      <p className="text-[color:var(--ink)] mt-0.5">{value}</p>
    </div>
  );
}
